import { useState } from 'react';

import pluralize from 'pluralize';

import {
  ActionsGroup,
  Alert,
  Bleed,
  Button,
  DialogV2,
  Icons,
  Inset,
  LinkBehaviour,
  useCloseDialog,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { OrganizationAvatar } from '@cast/shared-organization-ui';
import { Organization } from '@cast/types';
import { Member } from '@cast/features-organization-shared-types';

import { useRemoveUserFromOrganizationWithValidationMutation } from 'hooks/mutations/organization/useRemoveUserFromOrganizationWithValidationMutation';
import { useRemoveUsersFromOrganizationMutation } from 'hooks/mutations/organization/useRemoveUsersFromOrganizationMutation';
import { MemberList } from '../../access-control/_components/MemberList';

export type RemoveUserFromOrganizationDialogProps = {
  organization: Organization;
  members: Member[];
  isMe: boolean;
  cannotLeave?: boolean;
  onDeleteOrganization?: () => void;
  onSuccess?: () => void;
};

export const RemoveUserFromOrganizationDialogContent = ({
  organization,
  members,
  isMe,
  cannotLeave = false,
  onDeleteOrganization,
  onSuccess,
}: RemoveUserFromOrganizationDialogProps) => {
  const closeDialog = useCloseDialog();
  const [membersToRemove, setMembersToRemove] = useState<Member[]>(members);

  const { mutate: removeMyself, isPending: isRemovingMyself } =
    useRemoveUserFromOrganizationWithValidationMutation(() => {
      onSuccess?.();
      closeDialog();
      if (isMe) window.location.href = window.location.origin;
    });

  const { mutate: removeOthers, isPending: isRemovingOthers } =
    useRemoveUsersFromOrganizationMutation(() => {
      onSuccess?.();
      closeDialog();
    });

  const handleRemove = () => {
    if (isMe) {
      removeMyself({
        orgId: organization.id,
        userId: membersToRemove[0].id,
      });
    } else {
      removeOthers({
        orgId: organization.id,
        users: membersToRemove.map((member) => member.id),
      });
    }
  };

  return (
    <DialogV2
      testId={
        cannotLeave
          ? 'cannot-leave-organization-dialog'
          : 'remove-user-from-organization-dialog'
      }
      title={
        cannotLeave || isMe
          ? 'Leave organization'
          : `Remove ${pluralize(
              'user',
              membersToRemove.length
            )} from Organization`
      }
      metadata={
        <Stack direction="row" mt={8} gap={6}>
          <OrganizationAvatar organizationId={organization.id} size="xs" />
          <Typography color={tokens.theme.fg.structural.primary} variant="A1">
            {organization.name}
          </Typography>
        </Stack>
      }
      footerDivider
      endActions={
        cannotLeave ? (
          <Button
            variant="secondary"
            onClick={() => closeDialog()}
            testId="cannot-leave-close-button"
          >
            Close
          </Button>
        ) : (
          <ActionsGroup>
            <Button variant="secondary" onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={isRemovingMyself || isRemovingOthers}
              onClick={handleRemove}
              testId="remove-button"
              endIcon={isMe ? <Icons.ArrowSquareOut /> : undefined}
            >
              {isMe ? 'Leave' : 'Remove'}
            </Button>
          </ActionsGroup>
        )
      }
    >
      <Inset mb={cannotLeave || isMe ? 16 : 0}>
        {cannotLeave ? (
          <Stack gap={12}>
            <Typography
              color={tokens.theme.fg.structural.primary}
              variant="P12R"
            >
              You cannot leave this organization. Organizations require at least
              one user with the Owner role, and you are currently the only
              Owner.
            </Typography>

            <Box component="ul" sx={{ pl: 18, m: 0 }}>
              <Typography
                component="li"
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
                mb={8}
              >
                <Typography
                  color={tokens.theme.fg.structural.primary}
                  component="span"
                  variant="P12M"
                >
                  Add another Owner:
                </Typography>
                <br />
                Invite another user and assign them the Owner role
              </Typography>
              <Typography
                component="li"
                variant="P12R"
                color={tokens.theme.fg.structural.secondary}
              >
                <LinkBehaviour
                  component="span"
                  variant="P12M"
                  onClick={() => {
                    closeDialog();
                    onDeleteOrganization?.();
                  }}
                  disabled={!onDeleteOrganization}
                >
                  Delete organization:
                </LinkBehaviour>
                <br />
                Permanently remove the organization if it&apos;s no longer
                required
              </Typography>
            </Box>
          </Stack>
        ) : isMe ? (
          <Stack gap={12}>
            <Typography
              color={tokens.theme.fg.structural.primary}
              variant="P12R"
            >
              Are you sure you want to leave the{' '}
              <Typography
                color={tokens.theme.fg.structural.primary}
                component="span"
                variant="P12M"
              >
                {organization.name}
              </Typography>{' '}
              organization?
            </Typography>
            <Typography
              variant="P12R"
              color={tokens.theme.fg.structural.secondary}
            >
              This action cannot be undone and all associated permissions will
              be removed.
            </Typography>
          </Stack>
        ) : (
          <Stack>
            <Alert color="warning" size="sm" sx={{ mb: 16 }}>
              By continuing, you are also deleting all API keys associated with
              these users.
            </Alert>

            <Typography
              color={tokens.theme.fg.structural.primary}
              variant="h6"
              pb={8}
            >
              {membersToRemove.length}{' '}
              {pluralize('user', membersToRemove.length)} selected
            </Typography>

            <Bleed>
              <MemberList
                members={membersToRemove}
                showRemoveButton={membersToRemove.length > 1}
                onMemberRemoveFromList={(memberId) => {
                  setMembersToRemove((current) =>
                    current.filter((m) => m.id !== memberId)
                  );
                }}
              />
            </Bleed>
          </Stack>
        )}
      </Inset>
    </DialogV2>
  );
};
